import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ComprobanteElectronico } from '../types/comprobante.types';
import { ComprobanteElectronicoService } from '../services/comprobanteElectronicoService';

interface ComprobanteState {
  comprobantes: ComprobanteElectronico[];
  loading: boolean;
  error: string | null;

  fetchComprobantes: () => Promise<void>;
  enviarComprobante: (idFactura: number) => Promise<void>;
  consultarEstado: (id: number) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  comprobantes: [],
  loading: false,
  error: null,
};

export const useComprobanteStore = create<ComprobanteState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchComprobantes: async () => {
        set({ loading: true, error: null }, false, 'comprobante/fetchAll/start');
        try {
          const comprobantes = await ComprobanteElectronicoService.getAll();
          set({ comprobantes, loading: false }, false, 'comprobante/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar comprobantes' }, false, 'comprobante/fetchAll/error');
        }
      },

      enviarComprobante: async (idFactura: number) => {
        set({ loading: true, error: null }, false, 'comprobante/enviar/start');
        try {
          await ComprobanteElectronicoService.enviar(idFactura);
          set({ loading: false }, false, 'comprobante/enviar/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al enviar comprobante' }, false, 'comprobante/enviar/error');
          throw error;
        }
      },

      consultarEstado: async (id: number) => {
        set({ loading: true, error: null }, false, 'comprobante/consultar/start');
        try {
          const result = await ComprobanteElectronicoService.consultarEstado(id);
          if (result) {
            set((state) => ({
              comprobantes: state.comprobantes.map((c) =>
                c.idComprobanteElectronico === id ? result : c
              ),
              loading: false,
            }), false, 'comprobante/consultar/success');
          } else {
            set({ loading: false }, false, 'comprobante/consultar/notFound');
          }
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al consultar estado' }, false, 'comprobante/consultar/error');
        }
      },

      clearError: () => set({ error: null }, false, 'comprobante/clearError'),
      reset: () => set(initialState, false, 'comprobante/reset'),
    }),
    { name: 'comprobante-store' }
  )
);
